import moment from "moment";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { firstValueFrom, forkJoin } from "rxjs";
import { AppState } from "../../core";
import { activity_status } from "../../core/models/task-status";
import { StageDoc } from "../../core/schema/stage-schema";
import { StageService } from "../../core/services/stage.service";
import { ConfirmModalComponent } from "../../shared/components/confirm-modal/confirm-modal.component";
import { FesBaseComponent } from "../../shared/components/fes-base.component";
import { TableViewSort } from "../../shared/components/table-view/table-view-component-base";
import { Volunteer } from "../models/volunteer";
import { VolunteerPointViewRecord } from "../models/volunteer-point-record";
import { VolunteerTaskPointViewRecord } from "../models/volunteer-task-point-record";
import {
  VolunteerTaskRecord,
  VolunteerTaskViewRecord,
} from "../models/volunteer-task-record";
import { VolunteerPointViewService } from "../services/volunteer-point-view.service";
import { VolunteerTaskPointViewService } from "../services/volunteer-task-point-view.service";
import { VolunteerTaskViewService } from "../services/volunteer-task-view.service";
import { VolunteerTaskService } from "../services/volunteer-task.service";
import { VolunteerService } from "../services/volunteer.service";
import { VolunteerTaskSeriesComponent } from "../components/volunteer-task-series/volunteer-task-series.component";
import { FesRutTaskConfig, VolunteerTaskSeries } from "../models";
import { VolunteerTaskPointService } from "../services/volunteer-task-point.service";
import { VolunteerTaskPointTestParamsComponent } from "../volunteer-task-point/volunteer-task-point-test-params/volunteer-task-point-test-params.component";
import { ScoutPoint } from "../../core/models";

class TaskPointChoice {
  taskPoint: VolunteerTaskPointViewRecord;
  volPoint: VolunteerPointViewRecord;
  bSelected: boolean = false;
  scoutPoint?: ScoutPoint;
}

@Component({
  selector: "app-volunteer-task-edit",
  templateUrl: "./volunteer-task-edit.component.html",
  styleUrls: ["./volunteer-task-edit.component.scss"],
})
export class VolunteerTaskEditComponent
  extends FesBaseComponent
  implements OnInit, OnDestroy
{
  @Input("task") set taskSetter(taskRec: VolunteerTaskViewRecord) {
    this.setTask(taskRec);
  }
  @Input() stages: string[] = [];
  @Input() pathIds: string[] = [];
  @Input() doAfterSaveFn: () => void;
  @Input() doAfterDeleteFn: (deletedRec: VolunteerTaskRecord) => void;

  @Output() deleted$ = new EventEmitter<VolunteerTaskViewRecord>();

  @ViewChild("testForm") testForm: NgForm;
  @ViewChild("taskSeries") taskSeriesComponent: VolunteerTaskSeriesComponent;
  @ViewChild("taskPointTestParams") taskPointTestParamsComponent: VolunteerTaskPointTestParamsComponent;

  NOT_STARTED = activity_status.Id.NOT_STARTED;
  task: VolunteerTaskViewRecord;
  tmpTask: VolunteerTaskViewRecord = new VolunteerTaskViewRecord();

  buildingInfoArray: { id: string; name: string }[] = [];
  userInfoArray: { id: string; name: string }[] = [];
  buildingVolunteers: Volunteer[] = [];

  bProcessing = false;
  bLoadingPointIds = false;
  bLoadingPathIds = false;
  bNoWaypointsFound = false;
  testPointsModified = false;
  errorMsg: string = "";

  taskPoints: VolunteerTaskPointViewRecord[];
  taskPointChoices: TaskPointChoice[];
  taskPointChoicesOpen = false;
  taskPointsModified = false;
  bAvailPointsLoading = false;
  volunteers: Volunteer[];
  bLoadingVolunteers = false;
  taskConfig: FesRutTaskConfig;
  
  constructor(
    appState: AppState,
    protected editModal: BsModalRef,
    private bsModalService: BsModalService,
    private volunteerService: VolunteerService,
    private volunteerTaskViewService: VolunteerTaskViewService,
    private volunteerTaskService: VolunteerTaskService,
    private volunteerPointViewService: VolunteerPointViewService,
    private volunteerTaskPointService: VolunteerTaskPointService,
    private volunteerTaskPointViewService: VolunteerTaskPointViewService,
    private stageService: StageService
  ) {
    super(appState);
  }

  ngOnInit(): void {
    this.init().catch(e=>this.volunteerTaskService.publishError(e,this));
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  async init() {
    this.bProcessing = true;

    try {
      const buildings =
        await this.volunteerPointViewService.findAvailableBuildings();
      this.buildingInfoArray = buildings.map((b) => ({
        id: b.id,
        name: b.commonName,
      }));
      if (!this.tmpTask?.buildingId) {
        this.tmpTask.buildingId = buildings[0]?.id;
      }
      await this.findVolunteersForBuildingId();
      this.taskConfig = await this.volunteerTaskService.getRemoteConfig();
    } catch (error) {
      console.error(error);
      this.volunteerService.publishError(error, this);
    } finally {
      this.bProcessing = false;
    }
    const stageQuery = this.stageService.rxCollection.find();
    this.subscribe(stageQuery.$, (stages: StageDoc[]) => {
      this.stages = stages.map((value) => value.name);
      if (!this.tmpTask.stage) {
        this.tmpTask.stage = this.stages[0];
      }
    });
  }

  async findVolunteersForBuildingId() {
    console.log(
      `findVolunteersForBuildingId() buildingId: ${this.tmpTask.buildingId}`
    );
    const buildingId = this.tmpTask.buildingId;
    this.bLoadingVolunteers = true;
    try {
      this.volunteers = await this.volunteerService.findByBuildingId(
        buildingId
      );
      if (!this.tmpTask.volunteerId) {
        console.log(
          `Volunteer is empty for this task. Using first from list of available volunteers for building '${buildingId}'`
        );
        this.tmpTask.volunteerId = this.volunteers[0]?.id;
      } else if (
        !this.volunteers.find((v) => v.id === this.tmpTask.volunteerId)
      ) {
        console.log(
          `Volunteer '${this.volunteers[0]?.id}' of task is not in list of available volunteers for building '${buildingId}'`
        );
        this.tmpTask.volunteerId = this.volunteers[0]?.id;
      }
      console.log("Available volunteers:");
      console.dir(this.volunteers);
      console.log("Unsaved volunteer task:");
      console.dir(this.tmpTask);
      await this.initPointChoices();
    } catch (error) {
      this.errorMsg =
        this.volunteerService.extractErrorMsg(error) ||
        "Failed loading volunteers from REST service";
    }
    this.bLoadingVolunteers = false;
  }

  async initPointChoices() {
    this.bAvailPointsLoading = true;
    try {
      const sorts: TableViewSort[] = [{ key: "pointId", order: "ASC" }];
      const [availPtsResult, assignedTaskPtsResult] = await firstValueFrom(
        forkJoin([
          this.volunteerPointViewService.find({
            filters: [
              { mode: "=", key: "buildingId", value: this.tmpTask.buildingId },
              {
                mode: "=",
                key: "volunteerId",
                value: this.tmpTask.volunteerId,
              },
              { mode: "=", key: "deleted", value: "false" },
            ],
            sorts,
          }),
          this.volunteerTaskPointViewService.find({
            filters: [
              { mode: "=", key: "taskId", value: `${this.task.id}` },
              { mode: "=", key: "deleted", value: "false" },
            ],
            sorts,
          }),
        ])
      );
      this.taskPoints = assignedTaskPtsResult.rows;
      // exclude REF points
      const availPts = availPtsResult.rows.filter(
        (row) => row.pointId && !row.pointId.toUpperCase().endsWith("REF")
      );
      this.taskPointChoices = availPts.map((volPoint) => {
        let taskPoint = this.taskPoints.find(
          (taskPt) => volPoint.id == taskPt.volunteerPointId
        );
        const bSelected = !!taskPoint;
        if (!taskPoint) {
          taskPoint = {
            ...volPoint,
            id: null, // null means new task point needs to be added to DB on save
            taskId: this.task.id, // task.id may be null on new tasks (on save will have to add it)
            scoutId: null,
            volunteerPointId: volPoint.id,
            commonName: null,
            pointIdQr: null,
          };
        }
        return {
          taskPoint,
          bSelected,
          volPoint,
        };
      });
      const invalidTaskPoints = this.taskPoints.filter((taskPoint) =>
        availPts.every((pt) => pt.id != taskPoint.volunteerPointId)
      );
      invalidTaskPoints.forEach((invalidTaskPt) => {
        this.taskPointChoices.push({
          taskPoint: invalidTaskPt,
          volPoint: null,
          bSelected: true,
        });
      });
      this.taskPointChoices.sort((a, b) =>
        a.taskPoint.pointId?.localeCompare(b.taskPoint.pointId)
      );
      try {
        const taskPointsFromChoices = this.taskPointChoices.map(tpc=>tpc.taskPoint);
        await this.volunteerTaskPointViewService.updateScoutIds(taskPointsFromChoices);
        const scoutInfoByPointId = await this.volunteerTaskPointViewService.getScoutInfoForTaskPoints(
          taskPointsFromChoices, {}, (tp:VolunteerTaskPointViewRecord,_)=>tp.pointId
        )
        this.taskPointChoices.forEach( tpc => {
          tpc.scoutPoint = scoutInfoByPointId[tpc.volPoint.pointId]?.scoutPoint;
        });
      } catch (e:any){
        console.error('Could not resolve scout points for volunteer task points',e);        
      }
      //this.taskPointChoices.map(tpc=>tpc.volPoint.pointId)
    } catch (error) {
      this.errorMsg =
        this.volunteerPointViewService.extractErrorMsg(error) ||
        "Failed loading volunteer points from REST service";
    }
    this.bAvailPointsLoading = false;
  }

  series: VolunteerTaskSeries;

  setTask(taskRec: VolunteerTaskViewRecord) {
    console.log(`setTask()`);
    console.dir(taskRec);
    this.task = taskRec;
    this.tmpTask = JSON.parse(JSON.stringify(taskRec));
    if (!this.tmpTask.stage) {
      this.tmpTask.stage = this.stages[0];
    }
    if (this.tmpTask.scheduledDate) {
      // date input doesn't like the time included (ex. "2022-10-14T00:00:00.000Z")
      this.tmpTask.scheduledDate = this.tmpTask.scheduledDate.replace(
        /[T ].*/,
        ""
      );
    }
    if ( !this.tmpTask.id ) {
      // provide series defaults if new task
      this.tmpTask.seriesMaxCnt = VolunteerTaskSeries.MAX_CNT;
      this.tmpTask.scheduledDurationHrs = VolunteerTaskSeries.DEFAULT_TASK_DURATION_HRS;
      this.tmpTask.seriesTaskDurationHrs = VolunteerTaskSeries.DEFAULT_TASK_DURATION_HRS;
      this.tmpTask.seriesTaskIntervalHrs = VolunteerTaskSeries.DEFAULT_TASK_INTERVAL_HRS;
    }
    this.series = this.extractSeries(this.tmpTask);
  }

  extractSeries(taskViewRec: VolunteerTaskViewRecord ) {
    const series = new VolunteerTaskSeries();
    series.id = taskViewRec.seriesId;
    series.maxCnt = taskViewRec.seriesMaxCnt
    series.defaultDurationHrs = taskViewRec.scheduledDurationHrs || taskViewRec.seriesTaskDurationHrs;
    series.defaultIntervalHrs = taskViewRec.seriesTaskIntervalHrs;
    console.log(`extractSeries()`,taskViewRec,series);
    return series;
  }

  selectedPoint: VolunteerPointViewRecord;
  async onVolunteerPointSelected(volunteerPoint: VolunteerPointViewRecord) {
    this.selectedPoint = volunteerPoint;
    //ngModel.control.setValue(volunteerPoint.id);
    this.testForm.form.markAsDirty();
  }

  async onDelete() {
    this.errorMsg = "";
    try {
      const deletedTask = await this.volunteerTaskViewService.promptedDeleteTask(
        this.task
      );
      if (deletedTask) {
        this.doAfterDeleteFn && this.doAfterDeleteFn(deletedTask);
      }
    } catch (error) {
      console.error(error);
      this.errorMsg = this.volunteerTaskService.extractErrorMsg(error);
    }
  }

  getSelectedTaskPoints = () =>
    this.taskPointChoices
      .filter((choice) => choice.bSelected)
      .map((choice) => choice.taskPoint);
  //getExistingTaskPoints = () => this.taskPointChoices.map(choice=>!!choice.taskPoint.id)

  async onSave() {
    this.errorMsg = "";
    this.bProcessing = true;

    //
    // Resolve ambiguity in UI regarding "duration" changes.  It can apply to the series or just the task instance
    //
    if ( this.task.seriesId && this.task.scheduledDurationHrs != this.tmpTask.scheduledDurationHrs ) {
      const modalRef = this.bsModalService.show(ConfirmModalComponent, {
        ignoreBackdropClick: true,
        id: 202308014,
        initialState: {
          yesBtnClass: 'btn btn-primary m-2',
          noBtnClass: 'btn btn-primary m-2',
          onYes: async () => {
            // instance only so set series value back to original
            this.tmpTask.seriesTaskDurationHrs = this.task.seriesTaskDurationHrs;
          },
          yesBtnText: 'Instance',
          noBtnText: 'Series',
          question: `Apply task duration change to just this instance or include all new tasks in the series?`,
          title: "Duration Changed on Existing Task",
        },
      });
      await firstValueFrom(modalRef.onHidden)
    }

    try {
      const customTestParams = this.taskPointTestParamsComponent?.getFormData();
      const selectedTaskPoints = this.getSelectedTaskPoints();
      if ( selectedTaskPoints?.length == 0 && this.taskPointTestParamsComponent?.getChanges().length ) {
        let bCancel = true;
        const modalRef = this.bsModalService.show(ConfirmModalComponent, {
          ignoreBackdropClick: true,
          id: 202310009,
          initialState: {
            onYes: async () => {
              bCancel = false;
            },
            yesBtnText: 'Save',
            noBtnText: 'Cancel',
            question: `The current implementation cannot apply custom parameters if no test points are selected.  Save without custom parameters?`,
            title: "Custom test parameters not applicable",
          },
        });
        await firstValueFrom(modalRef.onHidden)
        if ( bCancel) {
          return;
        }
      }

      const { task, points: taskPoints } =
        await this.volunteerTaskViewService.saveWithPoints(
          { ...this.tmpTask, scheduledDate: moment.utc(this.tmpTask.scheduledDate).format() }, 
          selectedTaskPoints,
          customTestParams
        );
      this.setTask(task);
      await this.initPointChoices();
      console.dir(task);
      //this.editModal.hide();
      try {
        const scoutInfoByTaskPtId = await this.volunteerTaskPointViewService.buildScoutInfoForTaskPoints(taskPoints);
        // only assign visible photos for new task points (server response task points will have the new ID's)        
        let newTaskPoints = taskPoints.filter(stp=>selectedTaskPoints.find(tp=>tp.id==null&&stp.pointId==tp.pointId));
        await this.volunteerTaskPointService.assignVisiblePhotos({taskPoints:newTaskPoints,scoutInfoByTaskPtId});
      } catch ( e: any ) {
        console.error('Failed assigning visible photos for volunteer point(s)');
        this.volunteerTaskService.publishError(e,this,5*60);
      }
      this.doAfterSaveFn && this.doAfterSaveFn();
      this.testForm.form.markAsPristine();      
      this.updateTaskPointsModified();
      this.appState.notifySuccess(
        `Volunteer '${task.volunteerName}' test saved.`,
        this,
        3
      );
    } catch (error) {
      this.errorMsg = this.volunteerTaskViewService.extractErrorMsg(error);
      console.error(error);
      this.volunteerTaskViewService.publishError(error, this);
    } finally {
      this.bProcessing = false;
    }
  }

  onClose() {
    if (this.testForm.dirty || this.taskPointsModified) {
      const modalRef = this.bsModalService.show(ConfirmModalComponent, {
        ignoreBackdropClick: true,
        id: 202111011,
        initialState: {
          onYes: async () => {
            this.editModal.hide();
          },
          question: `Unsaved changes will be lost. Are you sure you wish to cancel?`,
          title: "Unsaved Data",
        },
      });
    } else {
      this.editModal.hide();
    }
  }

  async onBuildingIdChanged(buildingId: string) {
    await this.findVolunteersForBuildingId();
  }

  async onVolunteerIdChanged(volunteerId: string) {
    await this.initPointChoices();
  }

  async onSeriesMaxCntChange(maxCnt) {
    this.tmpTask.seriesMaxCnt = maxCnt;
  }

  async onSeriesIntervalChange(intervalHrs) {
    this.tmpTask.seriesTaskIntervalHrs = intervalHrs;
  }
  
  async onSeriesDurationChange(durationHrs) {
    // update both scheduleDurationHrs and seriesTaskDurationHrs (UI will confirm with user before saving)  
    this.tmpTask.scheduledDurationHrs = durationHrs;
    this.tmpTask.seriesTaskDurationHrs = durationHrs;
  }

  async onToggleTaskPtChoice(taskPtChoice: TaskPointChoice) {
    //console.log(`onToggleTaskPtChoice(${taskPtChoice.taskPoint.pointId})`);
    taskPtChoice.bSelected = !taskPtChoice.bSelected;
    this.updateTaskPointsModified();
  }

  updateTaskPointsModified = () =>
    (this.taskPointsModified = this.taskPointChoices.some((choice) =>
      choice.taskPoint.id ? !choice.bSelected : choice.bSelected
    ));
}
